export const MEDICAL_NECESSITY_EXAMPLE = `PRE-BILL LCD / NCD COVERAGE TEST INPUT
Module: Confirm LCD / NCD support before you bill
Use screen fields:
CPT / HCPCS: 72148, 11042
ICD-10 Supporting DX: M54.5, E11.9
Payer: Medicare / Novitas if available; otherwise leave payer blank

Patient: James R. Whitman
DOB: 1958-04-17
Date of Service: 2026-06-18
Encounter Type: Office visit / wound care follow-up
Provider: Priya Mehta, MD

CHIEF COMPLAINT
1. Worsening low back pain with left leg radiation.
2. Diabetic left plantar foot ulcer requiring wound evaluation.

HISTORY OF PRESENT ILLNESS
James R. Whitman is a 68-year-old male with type 2 diabetes mellitus presenting with worsening low back pain for approximately 9 weeks. Pain began after lifting a heavy box and has persisted despite conservative care. Pain is located in the lumbar spine and radiates down the left posterior thigh into the calf. He reports intermittent numbness and tingling in the left foot and difficulty walking more than one block due to pain. He has completed conservative management including NSAIDs, home exercise, activity modification, and 6 weeks of supervised physical therapy without meaningful improvement.

He denies bowel or bladder incontinence. He denies fever, unexplained weight loss, or recent trauma. Exam today shows positive left straight-leg raise, decreased sensation over the left lateral calf, and mild left ankle dorsiflexion weakness. MRI lumbar spine without contrast is ordered to evaluate suspected lumbar radiculopathy, nerve root compression, and possible spinal stenosis because symptoms persist despite conservative therapy and there are objective neurologic findings.

The patient also has a chronic diabetic ulcer on the left plantar foot under the first metatarsal head. The ulcer has been present for 5 weeks and has not closed with local wound care, offloading shoe, and daily dressing changes. Today the wound has devitalized slough and necrotic subcutaneous tissue. There is no exposed bone. No systemic fever is reported.

PAST MEDICAL HISTORY
- Type 2 diabetes mellitus
- Peripheral neuropathy
- Hypertension
- Chronic low back pain

CURRENT MEDICATIONS
- Metformin 1000 mg twice daily
- Gabapentin 300 mg nightly
- Lisinopril 10 mg daily
- Ibuprofen as needed

PHYSICAL EXAMINATION
General: Alert, ambulates with antalgic gait.
Lumbar spine: Limited flexion and extension due to pain.
Neurologic: Positive left straight-leg raise at 40 degrees. Decreased light-touch sensation left lateral calf. Mild left ankle dorsiflexion weakness, 4/5. Reflexes symmetric.
Skin/Wound: Left plantar foot ulcer under first metatarsal head. Pre-debridement size 2.8 cm x 1.9 cm x 0.3 cm. Wound bed has yellow slough and nonviable subcutaneous tissue. Mild surrounding callus. No exposed tendon or bone. No purulent drainage.

ASSESSMENT
1. Low back pain with left-sided radicular symptoms.
2. Suspected lumbar nerve root compression / spinal stenosis after failed conservative therapy.
3. Type 2 diabetes mellitus.
4. Diabetic left plantar foot ulcer with subcutaneous tissue involvement requiring excisional debridement.

PLAN / ORDERS
1. Order MRI lumbar spine without contrast, CPT 72148, due to persistent radicular low back pain lasting more than 6 weeks despite conservative therapy, with objective neurologic findings.
2. Perform selective/excisional debridement of left plantar diabetic foot ulcer down to and including subcutaneous tissue.
3. Continue offloading shoe and daily dressing changes.
4. Follow up in 1 week for wound reassessment.`;

export const CODING_INTELLIGENCE_EXAMPLE = `Coding Intelligence Module Input Case

Run a coding intelligence validation for the following professional claim lines. Validate NCCI/CCI procedure-to-procedure edits, MUE unit limits, and modifier usage against Medicare-style coding rules.

Claim context:
The encounter includes an established patient E/M service and a debridement service performed on the same date of service in an office setting. The coding team wants to know whether the codes, units, and modifiers are valid or whether the claim should be corrected before submission.

Service lines to check:

Line 1:
CPT/HCPCS code: 99213
Description: Established patient office or outpatient visit, low/moderate level E/M service
Units: 1
Modifier: None provided
Linked diagnosis codes: M54.5, E11.9

Line 2:
CPT/HCPCS code: 99214
Description: Established patient office or outpatient visit, higher level E/M service
Units: 1
Modifier: 25
Linked diagnosis codes: M54.5, E11.9

Line 3:
CPT/HCPCS code: 11042
Description: Debridement, subcutaneous tissue, first 20 square centimeters or less
Units: 2
Modifier: 59
Linked diagnosis codes: M54.5, E11.9

Diagnosis codes submitted:
M54.5 - Low back pain
E11.9 - Type 2 diabetes mellitus without complications

Requested validation:
Identify duplicate or mutually exclusive E/M coding issues, NCCI/CCI edit issues, MUE unit issues, modifier 25 support concerns, modifier 59 support concerns, and diagnosis-support concerns. Provide a corrected coding recommendation in plain language.`;

export const AUTH_ELIGIBILITY_EXAMPLE = `MEDICARE PRE-BILL LCD/NCD COVERAGE CHECK INPUT
Module: Medicare-only text-based LCD/NCD medical necessity checker
Input Type: Text only

============================================================
SCREEN / STRUCTURED INPUT
============================================================

Payer Type:
Medicare Part B

MAC / Jurisdiction:
Novitas

Date of Service:
2026-05-04

Place of Service:
11 — Office

Service Type:
Laboratory / Diagnostic test

CPT / HCPCS:
82306

CPT Description:
25-hydroxyvitamin D, single assay

ICD-10 Supporting Diagnoses:
E55.9 — Vitamin D deficiency, unspecified
N18.3 — Chronic kidney disease stage 3
M81.0 — Osteoporosis

Units:
1

ABN on File:
Unknown

Prior Test Date:
2026-01-20

Prior Test Result:
25-OH vitamin D = 18 ng/mL

Clinical Note Text:
Patient: Harold Gibson
DOB: 1949-09-30
Age/Sex: 76-year-old male
Date of Service: 2026-05-04
Encounter Type: Office / Outpatient Evaluation
Provider: Dr. L. Romano, MD — Internal Medicine

Chief Complaint:
Follow-up for chronic kidney disease, osteoporosis, and vitamin D deficiency monitoring.

History of Present Illness:
Mr. Gibson is a 76-year-old male with chronic kidney disease stage 3, osteoporosis, and documented vitamin D deficiency. His CKD stage 3 is supported by an eGFR of 42 mL/min, last measured on 2026-03-15. Osteoporosis was diagnosed in 2024 and the patient is currently treated with alendronate 70 mg weekly. He had a prior 25-hydroxyvitamin D level of 18 ng/mL on 2026-01-20 and has been taking cholecalciferol 2000 IU daily since January.

He reports diffuse bone pain, predominantly in the lower back and bilateral hips, ongoing for approximately 4 months. He denies recent falls or fractures.

Past Medical History:
- Chronic kidney disease stage 3
- Osteoporosis
- Vitamin D deficiency
- Hypertension
- Type 2 diabetes mellitus

Current Medications:
- Alendronate 70 mg weekly
- Cholecalciferol 2000 IU daily
- Lisinopril 10 mg daily
- Metformin 1000 mg twice daily

Physical Examination:
General: Well-appearing, no acute distress.
Musculoskeletal: Diffuse tenderness to palpation over lumbar spine and bilateral iliac crests. No deformity. Range of motion limited by pain.

Recent / Current Labs:
- Calcium: 8.9 mg/dL
- Phosphorus: 3.8 mg/dL
- Creatinine: 1.6 mg/dL
- eGFR: 42 mL/min

Assessment and Plan:
1. Vitamin D deficiency — prior 25-OH vitamin D level was 18 ng/mL on 2026-01-20. Patient has been on cholecalciferol supplementation for approximately 3.5 months. Order repeat 25-hydroxyvitamin D test, CPT 82306, to assess response to supplementation and guide dosing.
2. Chronic kidney disease stage 3 — stable, eGFR 42. Vitamin D monitoring is relevant for CKD mineral and bone disorder assessment.
3. Osteoporosis — patient is on bisphosphonate therapy with alendronate. Adequate vitamin D status is clinically important for calcium absorption and fracture risk reduction.
4. Follow up in 3 months with lab results.

Lab Order Text:
Order: 25-hydroxyvitamin D, single assay, CPT 82306.
Indication: Monitoring vitamin D status in a patient with documented vitamin D deficiency, chronic kidney disease stage 3, and osteoporosis on bisphosphonate therapy. Prior level was 18 ng/mL on 2026-01-20. Patient has been on supplementation for approximately 3.5 months. Last assay was more than 12 weeks prior. Testing is needed to guide supplementation dosing and assess adequacy for CKD mineral bone disease management.`;

export const CLAIMRIGHT_EXAMPLE = `{
  "id": "cr-test-01-ncci-bundle-em-debridement",
  "moduleId": "claim-right",
  "title": "NCCI Bundle: E/M + Wound Debridement Without Modifier 25",
  "desc": "Office visit 99214 billed same day as wound debridement 11042 without modifier 25 on the E/M. CPT 99214 is NCCI column 2 to 11042 column 1 -- the pair is bundled unless modifier 25 is appended to the E/M to indicate a separately identifiable service.",
  "payload": {
    "payload": {
      "claimInformation": {
        "serviceLines": [
          {
            "professionalService": {
              "procedureCode": "99214",
              "serviceUnitCount": 1,
              "procedureModifiers": []
            }
          },
          {
            "professionalService": {
              "procedureCode": "11042",
              "serviceUnitCount": 1,
              "procedureModifiers": []
            }
          }
        ],
        "healthCareCodeInformation": [
          { "diagnosisCode": "L97.521" }
        ]
      }
    }
  }
}`;

export const DENIAL_RESOLVE_EXAMPLE = `{
  "case_id": "dr-test-03-co50-medical-necessity",
  "domain": "Denial",
  "archetype": "medical_necessity",
  "complexity_level": "Medium",
  "summary": "Medicare (Novitas) denied a 25-hydroxyvitamin D test (82306) as not medically necessary under the applicable Local Coverage Determination (LCD). CARC CO-50 / RARC N115. The patient has documented vitamin D deficiency (E55.9), chronic kidney disease stage 3 (N18.3), and osteoporosis on bisphosphonate therapy (M81.0).",
  "denied_date_from": "2026-05-04",
  "denied_date_to": "2026-05-04",
  "claim_number": "CLM-2026-MN-0050",
  "patient": {
    "first_name": "Harold",
    "last_name": "Gibson",
    "dob": "1949-09-30",
    "sex_code": "M",
    "member_id": "1EG4TE5MK72"
  },
  "coverage": {
    "payer_id": "MEDICARE",
    "payer_name": "Medicare (Novitas)",
    "plan_type": "Medicare Part B"
  },
  "claim_lines": [
    {
      "line_no": 1,
      "cpt_or_hcpcs": "82306",
      "units": 1,
      "modifiers": [],
      "billed_amount": 312.00,
      "denied_amount": 312.00,
      "service_date": "2026-05-04",
      "place_of_service": "11",
      "icd10_pointers": ["E55.9", "N18.3", "M81.0"]
    }
  ],
  "denials": [
    {
      "carc": "50",
      "rarc": "N115",
      "line_no": 1,
      "payer_note": "These are non-covered services because this is not deemed a medical necessity by the payer. Decision based on a Local Coverage Determination (LCD).",
      "denied_amount": 312.00
    }
  ],
  "clinical_evidence": [
    {
      "note_type": "progress_note",
      "date": "2026-05-04",
      "author": "Dr. L. Romano, MD",
      "body": "Patient: Harold Gibson, 76-year-old male. CKD stage 3 (eGFR 42), osteoporosis on alendronate, vitamin D deficiency with prior level 18 ng/mL. On supplementation 3.5 months. Order repeat 25-OH vitamin D (82306) to assess response."
    }
  ]
}`;
